import React, { useState, useRef, useEffect, Component } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  TextField,
  MenuItem,
  Button,
  Pagination,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { ExpandMore, Download, Refresh, Clear } from '@mui/icons-material';
import { BsArrowLeftShort } from "react-icons/bs";
import MaterialReactTable from 'material-react-table';
import { getActivityLogs, getActivityLogsSummary } from '../api';
import useAuth from '../hooks/useAuth';
import ModalHeader from '../components/ModalHeader';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

// Error Boundary Component
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Don't log React internal errors or MUI specific errors
    if (!error.message?.includes('__reactFiber') && 
        !error.message?.includes('Permission denied') &&
        !error.message?.includes('ResizeObserver') &&
        !error.stack?.includes('ModalUnstyled')) {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      // Don't show error UI for React internal errors or MUI specific errors
      if (this.state.error?.message?.includes('__reactFiber') || 
          this.state.error?.message?.includes('Permission denied') ||
          this.state.error?.message?.includes('ResizeObserver') ||
          this.state.error?.stack?.includes('ModalUnstyled')) {
        return this.props.children;
      }
      
      return (
        <div className="flex justify-center items-center h-96">
          <Paper className="p-8 text-center">
            <Typography variant="h6" color="error">
              Something went wrong
            </Typography>
            <Typography variant="body1" className="mt-2">
              {this.state.error?.message || 'An unexpected error occurred'}
            </Typography>
            <Button 
              onClick={() => this.setState({ hasError: false, error: null })} 
              className="mt-4"
            >
              Try again
            </Button>
          </Paper>
        </div>
      );
    }

    return this.props.children;
  }
}

const UserLogs = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [selectedUserEmail, setSelectedUserEmail] = useState('');
  const [selectedAction, setSelectedAction] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [componentError, setComponentError] = useState(null);
  const tableRef = useRef();


  
  
  // Applied filters (only used for actual API calls)
  const [appliedFilters, setAppliedFilters] = useState({
    userEmail: '',
    action: '',
    startDate: '',
    endDate: '',
    page: 1
  });

  
  

  // Load initial data when component mounts
  useEffect(() => {
     if (!isAuthenticated || !user || !user.role || !['admin', 'user3', 'user1'].includes(user.role)) {
            window.location.replace('/select');
        }
    if (user && user.role === 'admin') {
      // Apply initial filters (empty filters for first load)
      setAppliedFilters({
        userEmail: '',
        action: '',
        startDate: '',
        endDate: '',
        page: 1
      });
    }
  }, [user]); // Only run when user changes

  // Function to apply current filter values
  const applyFilters = () => {
    // Validate user email
    const userEmailValue = selectedUserEmail.toString().trim();
    const validUserEmail = userEmailValue; // No validation needed for email, just trim
    
    setAppliedFilters({
      userEmail: validUserEmail,
      action: selectedAction,
      startDate: startDate,
      endDate: endDate,
      page: page
    });
  };

  // Check if filters have changed from applied ones
  const hasUnappliedChanges = () => {
    const userEmailValue = selectedUserEmail.toString().trim();
    const validUserEmail = userEmailValue;

    return appliedFilters.userEmail !== validUserEmail ||
           appliedFilters.action !== selectedAction ||
           appliedFilters.startDate !== startDate ||
           appliedFilters.endDate !== endDate;
  };

  // Clear all filters
  const clearFilters = () => {
    setSelectedUserEmail('');
    setSelectedAction('');
    setStartDate('');
    setEndDate('');
    setPage(1);
  };

  // Remove the debouncing effect since we're not auto-fetching anymore
  // useEffect(() => {
  //   const timer = setTimeout(() => {
  //     const userIdValue = selectedUserId.toString().trim();
  //     if (userIdValue === '' || (Number(userIdValue) > 0 && Number.isInteger(Number(userIdValue)))) {
  //       setDebouncedUserId(selectedUserId);
  //     } else {
  //       setDebouncedUserId('');
  //     }
  //   }, 500);
  //   return () => clearTimeout(timer);
  // }, [selectedUserId]);

  // Check if user has admin permission
  const hasAdminPermission = () => {
    try {
      return Boolean(user && user.role === 'admin');
    } catch (error) {
      console.error('Error checking admin permission:', error);
      return false;
    }
  };

   

    // Fetch activity logs with proper error handling
  const { data: logsData, isLoading, error, refetch } = useQuery(
    ['activity-logs', appliedFilters.page, limit, appliedFilters.userEmail, appliedFilters.action, appliedFilters.startDate, appliedFilters.endDate],
    async () => {
      try {
        // Only pass userEmail if it's not empty
        const validUserEmail = appliedFilters.userEmail && appliedFilters.userEmail.trim() ? appliedFilters.userEmail.trim() : undefined;
        return await getActivityLogs({ 
          page: appliedFilters.page, 
          limit, 
          userEmail: validUserEmail,
          action: appliedFilters.action || undefined,
          startDate: appliedFilters.startDate || undefined,
          endDate: appliedFilters.endDate || undefined
        });
      } catch (error) {
        console.error('Activity logs fetch error:', error);
        throw error;
      }
    },
    {
      enabled: Boolean(user && hasAdminPermission()),
      refetchOnWindowFocus: false,
      retry: 1,
      onError: (error) => {
        console.error('Activity logs query error:', error);
        // Filter out React internal errors
        if (error && error.message && 
            !error.message.includes('__reactFiber') && 
            !error.message.includes('Permission denied')) {
          setComponentError(error);
        }
      }
    }
  );

  // Fetch summary data
  const { data: summaryData } = useQuery(
    ['activity-logs-summary', appliedFilters.startDate, appliedFilters.endDate],
    async () => {
      try {
        return await getActivityLogsSummary({ 
          startDate: appliedFilters.startDate || undefined, 
          endDate: appliedFilters.endDate || undefined 
        });
      } catch (error) {
        console.error('Failed to fetch activity logs summary:', error);
        return null;
      }
    },
    {
      enabled: Boolean(user && hasAdminPermission() && (appliedFilters.startDate || appliedFilters.endDate)),
      refetchOnWindowFocus: false,
      retry: 1,
      onError: (error) => {
        console.error('Activity logs summary query error:', error);
      }
    }
  );

  // Error handling effect with better error catching
  useEffect(() => {
    const handleUnhandledError = (event) => {
      console.error('Unhandled error in UserLogs:', event.error);
      // Don't set component error for React internal errors or MUI specific errors
      if (event.error && event.error.message && 
          !event.error.message.includes('__reactFiber') && 
          !event.error.message.includes('Permission denied') &&
          !event.error.message.includes('ResizeObserver') &&
          !event.error.stack?.includes('ModalUnstyled')) {
        setComponentError(event.error);
      }
    };

    const handleUnhandledRejection = (event) => {
      console.error('Unhandled promise rejection in UserLogs:', event.reason);
      // Don't set component error for React internal errors or MUI specific errors
      if (event.reason && event.reason.message && 
          !event.reason.message.includes('__reactFiber') && 
          !event.reason.message.includes('Permission denied') &&
          !event.reason.message.includes('ResizeObserver') &&
          !event.reason.stack?.includes('ModalUnstyled')) {
        setComponentError(event.reason);
      }
    };

    window.addEventListener('error', handleUnhandledError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    return () => {
      window.removeEventListener('error', handleUnhandledError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Early return if user is not loaded yet
  if (!user) {
    return (
      <div className="flex justify-center items-center h-96">
        <CircularProgress size={60} />
        <Typography variant="h6" className="ml-4">
          Loading...
        </Typography>
      </div>
    );
  }

  // Column definitions for the table
  const columns = [
    {
      accessorKey: 'id',
      header: 'ID',
      size: 80,
    },
    {
      accessorKey: 'userEmail',
      header: 'User Email',
      size: 200,
    },
    {
      accessorKey: 'userType',
      header: 'User Type',
      size: 120,
      Cell: ({ cell }) => {
        const userType = cell.getValue();
        if (!userType) return '-';
        return (
          <Chip 
            label={userType} 
            color={userType === 'admin' ? 'primary' : 'default'}
            size="small"
          />
        );
      },
    },
    {
      accessorKey: 'action',
      header: 'Action',
      size: 100,
      Cell: ({ cell }) => {
        const action = cell.getValue();
        if (!action) return '-';
        
        let color = 'default';
        if (action === 'CREATE') color = 'success';
        else if (action === 'UPDATE') color = 'warning';
        else if (action === 'DELETE') color = 'error';
        
        return (
          <Chip 
            label={action} 
            color={color}
            size="small"
          />
        );
      },
    },
    {
      accessorKey: 'tableName',
      header: 'Table',
      size: 100,
    },
    {
      accessorKey: 'recordId',
      header: 'Record ID',
      size: 100,
    },
    {
      accessorKey: 'changedFields',
      header: 'Changed Fields',
      size: 200,
      Cell: ({ cell }) => {
        const fields = cell.getValue();
        if (!fields) return '-';
        try {
          // Handle both array and string formats
          if (Array.isArray(fields)) {
            return fields.join(', ');
          } else if (typeof fields === 'string') {
            const fieldArray = JSON.parse(fields);
            return Array.isArray(fieldArray) ? fieldArray.join(', ') : fields;
          }
          return String(fields);
        } catch {
          return String(fields) || '-';
        }
      },
    },
    {
      accessorKey: 'oldValues',
      header: 'Old Values',
      size: 200,
      enableHiding: true,
      Cell: ({ cell }) => {
        const oldValues = cell.getValue();
        if (!oldValues) return '-';
        try {
          if (typeof oldValues === 'object') {
            const keys = Object.keys(oldValues);
            if (keys.length === 0) return 'Empty';
            if (keys.length === 1) {
              const key = keys[0];
              const value = String(oldValues[key]);
              return `${key}: ${value.length > 20 ? value.substring(0, 20) + '...' : value}`;
            }
            return `${keys.length} fields changed`;
          }
          const stringValue = String(oldValues);
          return stringValue.length > 30 ? `${stringValue.substring(0, 30)}...` : stringValue;
        } catch {
          return String(oldValues) || '-';
        }
      },
    },
    {
      accessorKey: 'newValues',
      header: 'New Values',
      size: 200,
      enableHiding: true,
      Cell: ({ cell }) => {
        const newValues = cell.getValue();
        if (!newValues) return '-';
        try {
          if (typeof newValues === 'object') {
            const keys = Object.keys(newValues);
            if (keys.length === 0) return 'Empty';
            if (keys.length === 1) {
              const key = keys[0];
              const value = String(newValues[key]);
              return `${key}: ${value.length > 20 ? value.substring(0, 20) + '...' : value}`;
            }
            return `${keys.length} fields changed`;
          }
          const stringValue = String(newValues);
          return stringValue.length > 30 ? `${stringValue.substring(0, 30)}...` : stringValue;
        } catch {
          return String(newValues) || '-';
        }
      },
    },
    {
      accessorKey: 'ipAddress',
      header: 'IP Address',
      size: 150,
      Cell: ({ cell }) => cell.getValue() || '-',
    },
    {
      accessorKey: 'userAgent',
      header: 'User Agent',
      size: 200,
      Cell: ({ cell }) => {
        const userAgent = cell.getValue();
        if (!userAgent) return '-';
        
        // Extract browser info from user agent
        const browserMatch = userAgent.match(/(Firefox|Chrome|Safari|Edge)\/?([\d.]+)/);
        if (browserMatch) {
          return `${browserMatch[1]} ${browserMatch[2]}`;
        }
        
        // Fallback to truncated user agent
        return userAgent.length > 30 ? `${userAgent.substring(0, 30)}...` : userAgent;
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'Timestamp',
      size: 180,
      Cell: ({ cell }) => {
        const timestamp = cell.getValue();
        if (!timestamp) return '-';
        try {
          return new Date(timestamp).toLocaleString();
        } catch {
          return timestamp;
        }
      },
    },
  ];

  const handleExport = () => {
    if (!logsData?.data) return;

    const exportData = logsData.data.map(log => {
      const baseData = {
        ID: log.id,
        'User Email': log.userEmail,
        'User Type': log.userType,
        Action: log.action,
        Table: log.tableName,
        'Record ID': log.recordId,
        'Changed Fields': Array.isArray(log.changedFields) 
          ? log.changedFields.join(', ')
          : (() => {
              try {
                return JSON.parse(log.changedFields || '[]').join(', ');
              } catch {
                return log.changedFields || '';
              }
            })(),
        'IP Address': log.ipAddress || '',
        'User Agent': log.userAgent || '',
        Timestamp: new Date(log.createdAt).toLocaleString(),
      };

      // Add oldValues and newValues if they exist
      if (log.oldValues) {
        baseData['Old Values'] = typeof log.oldValues === 'object' 
          ? JSON.stringify(log.oldValues, null, 2)
          : log.oldValues;
      }
      if (log.newValues) {
        baseData['New Values'] = typeof log.newValues === 'object'
          ? JSON.stringify(log.newValues, null, 2)
          : log.newValues;
      }

      return baseData;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Activity Logs');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    saveAs(blob, `activity-logs-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  if (!hasAdminPermission()) {
    return (
      <div className="flex justify-center items-center h-96">
        <Paper className="p-8 text-center">
          <Typography variant="h6" color="error">
            Access Denied
          </Typography>
          <Typography variant="body1" className="mt-2">
            Only administrators can access user logs.
          </Typography>
        </Paper>
      </div>
    );
  }

  if (componentError) {
    return (
      <div className="flex justify-center items-center h-96">
        <Paper className="p-8 text-center">
          <Typography variant="h6" color="error">
            Component Error
          </Typography>
          <Typography variant="body1" className="mt-2">
            {componentError.message}
          </Typography>
          <Button onClick={() => setComponentError(null)} className="mt-4">
            Reset
          </Button>
        </Paper>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-96">
        <CircularProgress size={60} />
        <Typography variant="h6" className="ml-4">
          Loading Activity Logs...
        </Typography>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-96">
        <Paper className="p-8 text-center">
          <Typography variant="h6" color="error">
            Error Loading Logs
          </Typography>
          <Typography variant="body1" className="mt-2">
            {error.message || 'Failed to load activity logs.'}
          </Typography>
          <Button onClick={refetch} startIcon={<Refresh />} className="mt-4">
            Retry
          </Button>
        </Paper>
      </div>
    );
  }

  try {
    return (
      <ErrorBoundary>
        <div className="flex flex-col justify-between bg-white">

        <ModalHeader title="Διαλογή στην Πηγή" subtitle="User Activity Logs"></ModalHeader>

        <div className='items-center justify-between mx-auto mb-2 -mt-2 lg:flex lg:px-0'>
          <Link to={"/select"} className="flex text-app_green_light shadow-md  font-bold rounded-full text-lg px-10 py-2 text-center">
            <BsArrowLeftShort className='self-center w-6 h-6 text-app_green_light' /> Επιστροφή
          </Link>

          <div
            onClick={() => {
              logout()
            }}
            className="cursor-pointer ml-3 flex text-gray-400 shadow-md  font-bold rounded-full text-lg px-10 py-2 text-center">
            Αποσύνδεση
          </div>
        </div>

        <div className="2xl:w-[1600px] w-full px-10 py-2 mt-5 mx-auto sm:py-2 sm:px-6  lg:px-2">
          <Typography variant="h4" className="mb-6">
            User Activity Logs
          </Typography>

        {/* Summary Section */}
        {summaryData?.data && (
          <Accordion className="mb-6">
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">Activity Summary</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                {/* Action Summary */}
                {summaryData.data.actionSummary?.map((item, index) => (
                  <Grid item xs={12} sm={6} md={3} key={`action-${index}`}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" color="primary">
                          {item.count}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {item.action} Actions
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
                
                {/* User Type Summary */}
                {summaryData.data.userTypeSummary?.map((item, index) => (
                  <Grid item xs={12} sm={6} md={3} key={`usertype-${index}`}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" color="primary">
                          {item.count}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {item.userType} Users
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
                
                {/* Top User */}
                {summaryData.data.topUsers?.length > 0 && (
                  <>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" color="primary">
                            {summaryData.data.topUsers[0].userEmail}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Most Active User
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" color="primary">
                            {summaryData.data.topUsers[0].activityCount}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Activities by Top User
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </>
                )}
              </Grid>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Filters */}
        <Paper className="p-4 mb-6">
          {hasUnappliedChanges() && (
            <Box sx={{ mb: 2, p: 2, bgcolor: '#fff3cd', borderRadius: 1, border: '1px solid #ffeaa7' }}>
              <Typography variant="body2" color="#856404">
                ⚠️ You have unapplied filter changes. Click "Refresh" to apply them.
              </Typography>
            </Box>
          )}
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <ErrorBoundary>
                <TextField
                  fullWidth
                  label="User Email"
                  value={selectedUserEmail}
                  onChange={(e) => {
                    try {
                      const value = e.target.value;
                      setSelectedUserEmail(value);
                    } catch (error) {
                      console.error('Error setting user email:', error);
                      // Reset on error
                      setSelectedUserEmail('');
                    }
                  }}
                  type="email"
                  size="small"
                //   helperText="Enter user email to filter"
                  key={`useremail-input-${Date.now()}`} // Force re-render with unique key
                />
              </ErrorBoundary>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <ErrorBoundary>
                <TextField
                  fullWidth
                  select
                  label="Action"
                  value={selectedAction}
                  onChange={(e) => {
                    try {
                      setSelectedAction(e.target.value);
                    } catch (error) {
                      console.error('Error setting action:', error);
                      // Reset on error
                      setSelectedAction('');
                    }
                  }}
                  size="small"
                  key={`action-select-${selectedUserEmail}-${Date.now()}`} // Force re-render with unique key
                >
                  <MenuItem value="">All Actions</MenuItem>
                  <MenuItem value="CREATE">CREATE</MenuItem>
                  <MenuItem value="UPDATE">UPDATE</MenuItem>
                  <MenuItem value="DELETE">DELETE</MenuItem>
                </TextField>
              </ErrorBoundary>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button
                variant="outlined"
                onClick={handleExport}
                startIcon={<Download />}
                disabled={!logsData?.data?.length}
                fullWidth
              >
                Export
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={1}>
              <Button
                variant="outlined"
                onClick={clearFilters}
                startIcon={<Clear />}
                fullWidth
                color="secondary"
                size="small"
              >
                Clear
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={1}>
              <Button
                variant={hasUnappliedChanges() ? "contained" : "outlined"}
                onClick={() => {
                  applyFilters();
                  refetch();
                }}
                startIcon={<Refresh />}
                fullWidth
                color={hasUnappliedChanges() ? "primary" : "inherit"}
                sx={hasUnappliedChanges() ? { 
                  animation: 'pulse 1.5s infinite',
                  '@keyframes pulse': {
                    '0%': { opacity: 1 },
                    '50%': { opacity: 0.7 },
                    '100%': { opacity: 1 }
                  }
                } : {}}
              >
                {hasUnappliedChanges() ? "Apply" : "Refresh"}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Activity Logs Table */}
        <Paper>
          <MaterialReactTable
            key={`table-${appliedFilters.userEmail || 'all'}-${appliedFilters.action || 'all'}-${appliedFilters.startDate || 'nostart'}-${appliedFilters.endDate || 'noend'}-${appliedFilters.page}`} // More stable key
            columns={columns}
            data={logsData?.data || []}
            tableInstanceRef={tableRef}
            enableRowNumbers
            enableStickyHeader
            enableDensityToggle
            enableColumnResizing
            enableHiding
            initialState={{
              columnVisibility: {
                oldValues: false,
                newValues: false,
                //hide tableName column by default
                tableName: false,
                //hide ipAddress column by default
                ipAddress: false,
              },
            }}
            muiTableContainerProps={{
              sx: { maxHeight: '600px' }
            }}
            muiTableProps={{
              sx: { fontSize: '0.875rem' }
            }}
            renderDetailPanel={({ row }) => {
              // Add safety check for row data
              if (!row || !row.original) {
                return (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="error">
                      Error loading row details
                    </Typography>
                  </Box>
                );
              }

              try {
                return (
                  <Box sx={{ p: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>
                          Activity Details:
                        </Typography>
                        <Box sx={{ pl: 1 }}>
                          <Typography variant="body2">
                            <strong>User Email:</strong> {row.original.userEmail || 'N/A'}
                          </Typography>
                          <Typography variant="body2">
                            <strong>User ID:</strong> {row.original.userId || 'N/A'}
                          </Typography>
                          <Typography variant="body2">
                            <strong>IP Address:</strong> {row.original.ipAddress || 'N/A'}
                          </Typography>
                          <Typography variant="body2">
                            <strong>User Agent:</strong> {(() => {
                              const userAgent = row.original.userAgent;
                              if (!userAgent) return 'N/A';
                              const browserMatch = userAgent.match(/(Firefox|Chrome|Safari|Edge)\/?([\d.]+)/);
                              return browserMatch ? `${browserMatch[1]} ${browserMatch[2]}` : 'Unknown Browser';
                            })()}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Record ID:</strong> {row.original.recordId || 'N/A'}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Created:</strong> {row.original.createdAt ? new Date(row.original.createdAt).toLocaleString() : 'N/A'}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Updated:</strong> {row.original.updatedAt ? new Date(row.original.updatedAt).toLocaleString() : 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>
                          Changed Fields:
                        </Typography>
                        <Box sx={{ pl: 1 }}>
                          {(() => {
                            try {
                              const fields = row.original.changedFields;
                              if (Array.isArray(fields)) {
                                return fields.map((field, index) => (
                                  <Chip 
                                    key={index} 
                                    label={field} 
                                    size="small" 
                                    sx={{ mr: 0.5, mb: 0.5 }}
                                  />
                                ));
                              } else if (typeof fields === 'string') {
                                const fieldArray = JSON.parse(fields || '[]');
                                return fieldArray.map((field, index) => (
                                  <Chip 
                                    key={index} 
                                    label={field} 
                                    size="small" 
                                    sx={{ mr: 0.5, mb: 0.5 }}
                                  />
                                ));
                              }
                              return <Typography variant="body2">N/A</Typography>;
                            } catch {
                              return <Typography variant="body2">{row.original.changedFields || 'N/A'}</Typography>;
                            }
                          })()}
                        </Box>
                      </Grid>
                      
                      {/* Show old values if available */}
                      {row.original.oldValues && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" gutterBottom>
                            Old Values:
                          </Typography>
                          <pre style={{ 
                            fontSize: '0.75rem', 
                            whiteSpace: 'pre-wrap',
                            backgroundColor: '#f5f5f5',
                            padding: '8px',
                            borderRadius: '4px',
                            maxHeight: '200px',
                            overflow: 'auto'
                          }}>
                            {(() => {
                              try {
                                // Handle both object and string formats
                                if (typeof row.original.oldValues === 'object') {
                                  return JSON.stringify(row.original.oldValues, null, 2);
                                } else if (typeof row.original.oldValues === 'string') {
                                  return JSON.stringify(JSON.parse(row.original.oldValues), null, 2);
                                }
                                return row.original.oldValues;
                              } catch {
                                return row.original.oldValues;
                              }
                            })()}
                          </pre>
                        </Grid>
                      )}
                      
                      {/* Show new values if available */}
                      {row.original.newValues && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" gutterBottom>
                            New Values:
                          </Typography>
                          <pre style={{ 
                            fontSize: '0.75rem', 
                            whiteSpace: 'pre-wrap',
                            backgroundColor: '#f5f5f5',
                            padding: '8px',
                            borderRadius: '4px',
                            maxHeight: '200px',
                            overflow: 'auto'
                          }}>
                            {(() => {
                              try {
                                // Handle both object and string formats
                                if (typeof row.original.newValues === 'object') {
                                  return JSON.stringify(row.original.newValues, null, 2);
                                } else if (typeof row.original.newValues === 'string') {
                                  return JSON.stringify(JSON.parse(row.original.newValues), null, 2);
                                }
                                return row.original.newValues;
                              } catch {
                                return row.original.newValues;
                              }
                            })()}
                          </pre>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                );
              } catch (error) {
                console.error('Error rendering detail panel:', error);
                return (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="error">
                      Error rendering details
                    </Typography>
                  </Box>
                );
              }
            }}
          />
        </Paper>

        {/* Pagination */}
        {logsData?.pagination?.totalPages > 1 && (
          <Box display="flex" justifyContent="center" mt={4}>
            <Pagination
              count={logsData.pagination.totalPages}
              page={appliedFilters.page}
              onChange={(event, value) => {
                setPage(value);
                setAppliedFilters(prev => ({ ...prev, page: value }));
                refetch();
              }}
              color="primary"
              size="large"
            />
          </Box>
        )}
        </div>
        </div>
      </ErrorBoundary>
    );
  } catch (error) {
    setComponentError(error);
    return (
      <div className="flex justify-center items-center h-96">
        <Paper className="p-8 text-center">
          <Typography variant="h6" color="error">
            Rendering Error
          </Typography>
          <Typography variant="body1" className="mt-2">
            An error occurred while rendering the component.
          </Typography>
          <Button onClick={() => window.location.reload()} className="mt-4">
            Reload Page
          </Button>
        </Paper>
      </div>
    );
  }
};

export default UserLogs;
